import classNames from '../../utils/classNames'

// Placeholder bar colour: contrasts against the modal surface in both themes
// (matches the transaction/blog/news skeletons). The outer wrappers carry the
// `animate-pulse` so every bar pulses in sync.
const bar = 'bg-secondary-txt-50 dark:bg-tertiary rounded'

// Stands in for an underlined section title (Dinero / Tipo de retiro / …).
const Heading = ({ w }: { w: string }) => (
  <div className={classNames('h-4 mb-3', w, bar)} />
)

// Loading placeholder for the whole withdraw form, shown while KYC info loads.
// It mirrors the real layout so the modal does not jump when the form renders:
// left column = balance + the tall wagering/rollover card; right column = the
// two dropdowns and the amount presets stacked; the submit button + help/quota
// lines span below; and the far-right column = the transactions list.
const WithdrawFormSkeleton = () => (
  <div className="pb-6 xl:grid xl:grid-cols-[2fr_0.9fr] xl:gap-10 xl:items-start">
    <div className="mt-3 animate-pulse xl:mt-0">
      <div className="xl:grid xl:grid-cols-[1fr_1fr] xl:gap-x-6 xl:items-start">
        {/* Balance + wagering/rollover card. The card reproduces the real
            `Wagering` (completed) container structure exactly — same
            `rounded-lg border theme-border px-3 py-3 mb-2.5`, the 104px ring,
            the text lines and the saldo box — so its min-content (and thus how
            the `1fr 1fr` grid sizes this column) matches the loaded component
            and it resolves to the same width/height instead of a hardcoded box. */}
        <div className="mb-4">
          <Heading w="w-16" />
          <div className="flx-str-ctr gap-3 mb-4">
            <div className={classNames('h-[22px] w-6', bar)} />
            <div className={classNames('h-5 w-24', bar)} />
          </div>
          <div className="rounded-lg border border-solid theme-border px-3 py-3 mb-2.5">
            <div className="flex items-center gap-3">
              <div
                className={classNames(
                  'h-[104px] w-[104px] shrink-0 rounded-full',
                  bar,
                )}
              />
              <div className="flex-1 min-w-0">
                <div className={classNames('h-4 w-28 mb-1.5', bar)} />
                <div className={classNames('h-3 w-full mb-1', bar)} />
                <div className={classNames('h-3 w-3/4 mb-2', bar)} />
                <div className="rounded-lg border border-solid theme-border px-2 py-1.5">
                  <div className={classNames('h-3 w-24 mb-1.5', bar)} />
                  <div className={classNames('h-4 w-20', bar)} />
                </div>
              </div>
            </div>
            <div className="flx-ctr gap-1.5 mt-3">
              <div className={classNames('h-4 w-4 shrink-0 rounded-full', bar)} />
              <div className={classNames('h-3 w-40', bar)} />
            </div>
          </div>
        </div>
        {/* Two dropdowns + amount presets, stacked */}
        <div>
          {/* Type of withdrawal (dropdown — real row is h-[38px]) */}
          <div className="mb-4">
            <Heading w="w-28" />
            <div className={classNames('h-[38px] w-full', bar)} />
          </div>
          {/* Withdraw info (dropdown — real row is h-[38px]) */}
          <div className="mb-4">
            <Heading w="w-40" />
            <div className={classNames('h-[38px] w-full', bar)} />
          </div>
          {/* Amount presets + input + minimum-amount hint. The presets mirror
              the real WithdrawButtonGroup exactly — 4 full-width segments,
              border-divided with no gap, each carrying the same ~min-width the
              real "$50,000" labels impose. This keeps col2's min-content equal
              to the loaded form so the `1fr 1fr` (minmax(auto,1fr)) grid splits
              identically and the balance card on the left matches its width. */}
          <div className="mb-4">
            <Heading w="w-20" />
            <div className="flex mb-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="w-full h-9 min-w-[48px] border-0 border-r border-solid border-r-secondary-txt-20 bg-secondary-txt-50 last:border-r-0 dark:bg-tertiary xl:h-10"
                />
              ))}
            </div>
            <div className={classNames('h-6 w-full mb-2.5', bar)} />
            <div className={classNames('h-3 w-32', bar)} />
          </div>
        </div>
      </div>
      {/* Submit button + help link + remaining-withdrawals line, centered
          (matches the real ~mt-2 spacing between them). */}
      <div className="flex flex-col items-center gap-2 pt-2">
        <div
          className={classNames('h-[50px] w-40 xl:h-[56px] xl:w-[220px]', bar)}
        />
        <div className={classNames('h-3.5 w-56', bar)} />
        <div className={classNames('h-3 w-28', bar)} />
      </div>
    </div>
    {/* Transactions history */}
    <div className="mt-8 animate-pulse xl:mt-0">
      {/* Heading + refresh icon */}
      <div className="flx-btw-ctr mb-3">
        <div className={classNames('h-4 w-40', bar)} />
        <div className={classNames('h-4 w-4 shrink-0', bar)} />
      </div>
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 border-b border-solid border-b-secondary py-3">
          <div className="flex flex-col gap-2" style={{ flex: 1 }}>
            <div className={classNames('h-3 w-20', bar)} />
            <div className={classNames('h-3 w-24', bar)} />
          </div>
          <div className={classNames('h-3 w-16', bar)} style={{ flex: 1 }} />
          <div
            className="flex flex-col items-end gap-2"
            style={{ flex: 1 }}>
            <div className={classNames('h-3 w-12', bar)} />
            <div className={classNames('h-3 w-16', bar)} />
          </div>
        </div>
      ))}
      {/* "View more transactions" link */}
      <div className="flx-ctr pt-3">
        <div className={classNames('h-3.5 w-32', bar)} />
      </div>
    </div>
  </div>
)

export default WithdrawFormSkeleton
